// RemarksSummary.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../../const";
import Navbar from "../../components/Navbar";

const warehouseOptions = [
  { value: "SELECT BRANDS (I) PVT. LTD. (PUNE)", label: "Pune" },
  { value: "SELECT BRANDS (I) PVT. LTD. (KOLKATA)", label: "Kolkata" },
  { value: "DELHI WAREHOUSE", label: "Delhi-1" },
];

const dayOptions = [
  { value: 7, label: "Last 7 days" },
  { value: 14, label: "Last 14 days" },
  { value: 30, label: "Last 30 days" },
];

const RemarksSummary = () => {
  const token = localStorage.getItem("token");
  const storeId = localStorage.getItem("store");
  const username = localStorage.getItem("username");
  const isAdmin = username === "admin";

  const [days, setDays] = useState(7);
  const [warehouse, setWarehouse] = useState(
    "SELECT BRANDS (I) PVT. LTD. (PUNE)"
  );
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStart, setExportStart] = useState("");
  const [exportEnd, setExportEnd] = useState("");
  const [exportAllWh, setExportAllWh] = useState(false);
  const [exportWarehouse, setExportWarehouse] = useState("");

  // Helper to clear messages after 3 sec
  const clearMessageAfter = () => setTimeout(() => setMessage(null), 3000);

  const fetchSummary = async () => {
    if (!warehouse) return;

    setLoading(true);
    setMessage(null);

    try {
      const res = await axios.get(
        `${BASE_URL}/get_summary_remarks_data?days=${days}`,
        {
          headers: {
            Authorization: `Token ${token}`,
            Store: warehouse,
          },
        }
      );
      setData(res.data || []);
    } catch (err) {
      console.error("Error fetching summary:", err);
      setMessage({ type: "error", text: "Failed to load data." });
      clearMessageAfter();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, warehouse]);

  const handleDownload = async () => {
    if (!exportStart || !exportEnd) {
      setMessage({ type: "error", text: "Start and End dates are required." });
      clearMessageAfter();
      return;
    }

    setMessage(null);
    try {
      const params = new URLSearchParams({
        start_date: exportStart,
        end_date: exportEnd,
        all_wh: exportAllWh ? 1 : 0,
      });

      const storeToUse =
        isAdmin && exportWarehouse && !exportAllWh ? exportWarehouse : storeId;

      const response = await axios.get(
        `${BASE_URL}/combine_report1_export?${params.toString()}`,
        {
          headers: { Authorization: `Token ${token}`, Store: storeToUse },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const fname = `qc_report_${exportStart}_${exportEnd}${
        exportAllWh ? "_allwh" : ""
      }.csv`;
      link.href = url;
      link.setAttribute("download", fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setShowExportModal(false);
      setMessage({ type: "success", text: "Download started." });
      clearMessageAfter();
    } catch (err) {
      console.error("Error downloading report:", err);
      setMessage({ type: "error", text: "Failed to download report." });
      clearMessageAfter();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar title="Remarks Summary" />

      <div className="container mx-auto px-4 py-6">
        {/* Filters row */}
        <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
          <div className="flex items-center gap-2">
            <label className="text-gray-700 font-medium">Days:</label>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="border rounded-md px-3 py-1.5"
            >
              {dayOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-gray-700 font-medium">Warehouse:</label>
            <select
              value={warehouse}
              onChange={(e) => setWarehouse(e.target.value)}
              className="border rounded-md px-3 py-1.5"
            >
              {warehouseOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md"
            >
              Export
            </button>
          </div>
        </div>

        {/* Messages */}
        {message && (
          <div
            className={`mb-4 p-3 rounded-md ${
              message.type === "success"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Summary Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <p className="p-4">Loading...</p>
          ) : data.length === 0 ? (
            <p className="p-4 text-gray-600">No data found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
<thead className="bg-gray-50">
  <tr>
    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
      Date
    </th>

    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
      Total Entries
    </th>

    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
      Front Photos
    </th>

    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
      Group Photos
    </th>

    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
      Rejected Photos
    </th>

    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
      Total Photos
    </th>

    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
      Rejected Qty
    </th>

    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
      Remarks Received
    </th>

    <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase">
      Pending Remarks
    </th>
  </tr>
</thead>
<tbody className="bg-white divide-y divide-gray-100">
  {data.map((item, i) => (
    <tr key={i} className="hover:bg-gray-50">
      <td className="px-3 py-2 text-sm text-gray-900">
        {item.date}
      </td>

      <td className="px-3 py-2 text-center text-sm">
        {item.total_entries}
      </td>

      <td className="px-3 py-2 text-center text-sm">
        {item.front_photo_count}
      </td>

      <td className="px-3 py-2 text-center text-sm">
        {item.group_photo_count}
      </td>

      <td className="px-3 py-2 text-center text-sm">
        {item.rejected_photo_count}
      </td>

      <td className="px-3 py-2 text-center font-semibold text-blue-600">
        {item.total_photos}
      </td>

      <td className="px-3 py-2 text-center text-sm text-red-500">
        {item.rejected_qty}
      </td>

      <td className="px-3 py-2 text-center text-sm text-green-600">
        {item.total_remarks_received}
      </td>

      <td className="px-3 py-2 text-center font-semibold text-red-600">
        {item.pending_remarks_count}
      </td>
    </tr>
  ))}
</tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Export CSV</h3>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Start Date
                </label>
                <input
                  type="date"
                  value={exportStart}
                  onChange={(e) => setExportStart(e.target.value)}
                  className="mt-1 block w-full border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  End Date
                </label>
                <input
                  type="date"
                  value={exportEnd}
                  onChange={(e) => setExportEnd(e.target.value)}
                  className="mt-1 block w-full border rounded px-3 py-2"
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  id="all_wh"
                  type="checkbox"
                  checked={exportAllWh}
                  onChange={() => setExportAllWh((s) => !s)}
                />
                <label htmlFor="all_wh" className="text-sm">
                  All Warehouses
                </label>
              </div>

              {isAdmin && !exportAllWh && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Warehouse (override)
                  </label>
                  <select
                    value={exportWarehouse}
                    onChange={(e) => setExportWarehouse(e.target.value)}
                    className="mt-1 block w-full border rounded px-3 py-2"
                  >
                    <option value="">(use default store)</option>
                    {warehouseOptions.map((w) => (
                      <option key={w.value} value={w.value}>
                        {w.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 rounded border"
              >
                Cancel
              </button>
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-blue-600 text-white rounded"
              >
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemarksSummary;
